import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { cx } from '@linaria/core';
import { selectModelTypeFieldTypeInfo } from 'modules/Models/components/ModelForm/utils/utils';
import { isValidJSON } from 'utils/isValidJSON';
import { formContainer, inputContainer, inputStyle } from 'modules/Models/components/ModelForm/styles';
import { FormikInitialValues } from 'modules/Models/components/ModelForm/utils/types';
import { InputField } from 'components/ui_library/Formik';
import { Alert } from 'components/Alert';
import { FormLabel } from './FormLabel';

type JSONInputFormElementProps = React.InputHTMLAttributes<HTMLInputElement> & { path: string };

export const JSONInputFormElement = ({ path }: JSONInputFormElementProps) => {
  const { values, setFieldValue } = useFormikContext();
  const { fieldValueMap } = values as FormikInitialValues;
  const { description = '', placeholder = '' } = selectModelTypeFieldTypeInfo(path, values as FormikInitialValues);
  const formInputValue = typeof fieldValueMap[path] === 'object' ? JSON.stringify(fieldValueMap[path], undefined, 4) : fieldValueMap[path];
  const [validJSON, setIsValidJSON] = useState(true);

  const setError = (showError: boolean) => {
    if (showError) {
      setIsValidJSON(false);
      setFieldValue('errorDetails', 'Please check the form for incorrect values');
      setFieldValue('errorDescription', 'Invalid fields');
    } else {
      setIsValidJSON(true);
      setFieldValue('errorDetails', undefined);
      setFieldValue('errorDescription', undefined);
    }
  };

  const setJsonFieldValue = useCallback(
    (value: string) => {
      if (isValidJSON(value)) {
        setError(false);
        setFieldValue('fieldValueMap', { ...fieldValueMap, [path]: JSON.parse(value) });
      } else {
        setError(true);
        setFieldValue('fieldValueMap', { ...fieldValueMap, [path]: value });
      }
    },
    [fieldValueMap, path],
  );

  const onChange = useCallback(
    (e) => {
      setJsonFieldValue(e.target.value);
    },
    [setJsonFieldValue],
  );

  return (
    <div className={formContainer}>
      <div className={inputContainer}>
        <FormLabel path={path} description={description as string} />
        <div className={cx(inputStyle, 'json-input')}>
          <InputField
            name="jsonInput"
            type="text"
            isMultiLine
            id={path}
            placeholder={placeholder}
            value={formInputValue as string}
            onChange={onChange}
          />
          {!validJSON && <Alert type="error">Please enter valid JSON</Alert>}
        </div>
      </div>
    </div>
  );
};
